import { useEffect } from 'react';
import { toast } from 'sonner';

export const AssetLoadingErrorHandler = () => {
  useEffect(() => {
    let failedAssets = 0;
    const maxFailures = 3;

    const reloadPage = () => {
      window.location.reload();
    };

    const clearCachesAndReload = () => {
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        }).finally(reloadPage);
      } else {
        reloadPage();
      }
    };

    const handleError = (event: ErrorEvent | Event) => {
      const target = event.target as HTMLElement;
      
      if (target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK' || target.tagName === 'IMG')) {
        failedAssets++;
        const src = (target as HTMLScriptElement | HTMLImageElement).src || (target as HTMLLinkElement).href;
        console.error('Asset loading failed:', src);

        // If we have multiple asset failures, suggest a refresh
        if (failedAssets >= maxFailures) {
          toast.error('Multiple assets failed to load. This may be a caching issue.', {
            duration: 10000,
            action: {
              label: 'Refresh Page',
              onClick: clearCachesAndReload
            }
          });
        }
      }
    };

    // Listen for script and link loading errors
    window.addEventListener('error', handleError, true);

    // Also handle unhandled promise rejections from dynamic imports
    const handleRejection = (event: PromiseRejectionEvent) => {
      if (event.reason && event.reason.message && 
          (event.reason.message.includes('Loading chunk') || 
           event.reason.message.includes('Failed to fetch') ||
           event.reason.message.includes('404') ||
           event.reason.message.includes('module'))) {
        console.error('Dynamic import failed:', event.reason);
        failedAssets++;
        
        // Immediate cache clear for chunk loading failures
        if (event.reason.message.includes('Loading chunk') || 
            event.reason.message.includes('404')) {
          toast.error('Critical module loading failure detected. Clearing cache and reloading...', {
            duration: 3000,
            action: {
              label: 'Reload Now',
              onClick: clearCachesAndReload
            }
          });
          // Auto-reload after 3 seconds
          setTimeout(clearCachesAndReload, 3000);
        } else if (failedAssets >= maxFailures) {
          toast.error('Failed to load application modules. Clearing cache...', {
            action: {
              label: 'Reload',
              onClick: clearCachesAndReload
            }
          });
        }
      }
    };

    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return null;
};